
import React, { useState, useEffect, useRef } from 'react';
import { Clock, CheckCircle, Trophy, BookOpen, AlertCircle, FolderOpen, CornerDownRight, Search, X, Tag, Star, ListFilter, PenTool, Eraser, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Question, Folder } from '../data/types';
import { getTags } from '../utils';

// --- Drawing Helper Functions (Local to QuizViews) ---
const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
};

const getEraserCursor = (size: number) => {
    const cursorSize = size * 2 + 2; 
    const center = cursorSize / 2;
    const svg = `
        <svg width="${cursorSize}" height="${cursorSize}" viewBox="0 0 ${cursorSize} ${cursorSize}" xmlns="http://www.w3.org/2000/svg">
            <circle cx="${center}" cy="${center}" r="${size}" fill="rgba(255, 255, 255, 0.3)" stroke="black" stroke-width="1.5"/>
            <circle cx="${center}" cy="${center}" r="${1}" fill="black" />
        </svg>
    `;
    const encoded = encodeURIComponent(svg);
    return `url('data:image/svg+xml;charset=utf-8,${encoded}') ${center} ${center}, auto`;
};

const ERASER_SIZES = [8, 16, 32];

export const WelcomeView = ({ startQuiz, folders, questions, rootId, selectedQuizFolderId, setSelectedQuizFolderId, selectedCategories, setSelectedCategories }: any) => {
  const [tagSearch, setTagSearch] = useState('');
  const [questionCount, setQuestionCount] = useState(10);

  const getQuestionsInFolderRecursive = (folderId: string | null): Question[] => {
      let result = questions.filter((q: Question) => (q.folderId || null) === (folderId || null));
      const subFolders = folders.filter((f: Folder) => (f.parentId || null) === (folderId || null));
      subFolders.forEach((sub: Folder) => {
          result = [...result, ...getQuestionsInFolderRecursive(sub.id)];
      });
      return result;
  };

  const activeRootId = rootId || 'grade-private'; // fallback

  const questionsInScope = (selectedQuizFolderId === activeRootId || selectedQuizFolderId === null)
      ? getQuestionsInFolderRecursive(activeRootId)
      : getQuestionsInFolderRecursive(selectedQuizFolderId);
  
  // Filter by tags if needed to determine true availability
  const finalFilteredQuestions = selectedCategories.length > 0 
      ? questionsInScope.filter((q: Question) => {
          const qTags = getTags(q.category);
          return selectedCategories.some((selectedCat: string) => qTags.includes(selectedCat));
      })
      : questionsInScope;

  const maxQuestions = finalFilteredQuestions.length;

  useEffect(() => {
      if (questionCount > maxQuestions && maxQuestions > 0) {
          setQuestionCount(maxQuestions);
      } else if (maxQuestions > 0 && questionCount === 0) {
          setQuestionCount(Math.min(10, maxQuestions));
      }
  }, [maxQuestions, selectedCategories, selectedQuizFolderId]);

  const availableCategories: string[] = Array.from(new Set(
      questionsInScope.flatMap((q: Question) => getTags(q.category))
  )).sort() as string[];

  const filteredCategories = availableCategories.filter(cat => 
    cat.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const toggleCategory = (cat: string) => {
      setSelectedCategories((prev: string[]) => 
        prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
      );
  };

  // Get only first level folders under the active root
  const subjectFolders = folders.filter((f: Folder) => f.parentId === activeRootId);
  const totalGradeQuestions = getQuestionsInFolderRecursive(activeRootId).length;

  return (
    <div className="flex-1 bg-fudan-light overflow-y-auto">
      <div className="min-h-full flex flex-col items-center justify-center p-4 py-10">
        <div className="bg-white max-w-2xl w-full p-8 rounded-2xl shadow-xl text-center border-t-8 border-fudan-blue animate-fade-in-up my-4">
          <h1 className="text-4xl font-serif font-bold text-fudan-blue mb-4 tracking-wide">
            模擬測驗
          </h1>
          <p className="text-gray-600 mb-8 text-lg">
            請選擇測驗範圍並準備開始。
          </p>

          <div className="mb-6 text-left max-w-sm mx-auto">
              <label className="block text-gray-700 font-bold mb-2">步驟 1：選擇測驗科目/範圍</label>
              <div className="relative">
                  <FolderOpen className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <select 
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-fudan-blue outline-none appearance-none"
                      value={selectedQuizFolderId || activeRootId}
                      onChange={(e) => {
                          // If selecting activeRootId (ALL), pass that ID specifically instead of null, to allow clearer logic
                          setSelectedQuizFolderId(e.target.value);
                          setTagSearch(''); 
                      }}
                  >
                      <option value={activeRootId}>全部科目 (共 {totalGradeQuestions} 題)</option>
                      {subjectFolders.map((f: Folder) => {
                          const count = getQuestionsInFolderRecursive(f.id).length;
                          return (
                              <option key={f.id} value={f.id}>
                                  📂 {f.name} (含子目錄約 {count} 題)
                              </option>
                          );
                      })}
                  </select>
                  <CornerDownRight className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
          </div>

          <div className="mb-6 text-left max-w-sm mx-auto">
               <label className="block text-gray-700 font-bold mb-2 flex items-center justify-between">
                   <span>步驟 2：篩選題型標籤</span>
                   <span className="text-xs font-normal text-gray-500">(可複選)</span>
               </label>
               
               <div className="relative mb-2">
                   <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                   <input 
                      type="text"
                      placeholder="搜尋標籤..."
                      value={tagSearch}
                      onChange={(e) => setTagSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fudan-blue"
                   />
               </div>

               <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[60px] max-h-[150px] overflow-y-auto">
                   {availableCategories.length === 0 && <span className="text-gray-400 text-sm">此資料夾下無題目</span>}
                   {filteredCategories.length === 0 && availableCategories.length > 0 && <span className="text-gray-400 text-sm">沒有符合的標籤</span>}
                   
                   {filteredCategories.map((cat: string) => {
                       const isSelected = selectedCategories.includes(cat);
                       return (
                           <button
                               key={cat}
                               onClick={() => toggleCategory(cat)}
                               className={`px-3 py-1.5 text-sm rounded-full border transition-all flex items-center ${
                                   isSelected 
                                   ? 'bg-fudan-blue text-white border-fudan-blue shadow-md' 
                                   : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                               }`}
                           >
                               {cat}
                               {isSelected && <CheckCircle className="w-3 h-3 ml-1" />}
                           </button>
                       );
                   })}
               </div>
               {selectedCategories.length > 0 && (
                   <button onClick={() => setSelectedCategories([])} className="text-xs text-red-500 mt-2 hover:underline flex items-center">
                      <X className="w-3 h-3 mr-1" /> 清除所有篩選
                   </button>
               )}
          </div>

          <div className="mb-8 text-left max-w-sm mx-auto">
              <label className="block text-gray-700 font-bold mb-2 flex justify-between items-center">
                  <span>步驟 3：設定題數</span>
                  <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full font-bold">
                      每題 2 分鐘
                  </span>
              </label>
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center text-gray-500">
                          <ListFilter className="w-5 h-5 mr-2" />
                          <span className="text-sm">符合條件共 <strong className="text-gray-800">{maxQuestions}</strong> 題</span>
                      </div>
                      <div className="text-fudan-blue font-bold text-xl">
                          {questionCount} <span className="text-sm font-normal text-gray-500">題</span>
                      </div>
                  </div>
                  
                  <input 
                      type="range"
                      min="1"
                      max={Math.max(1, maxQuestions)}
                      value={questionCount}
                      onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-fudan-blue mb-3"
                      disabled={maxQuestions === 0}
                  />
                  
                  <div className="flex justify-between text-xs text-gray-400">
                      <span>1</span>
                      <span className="font-bold text-gray-500">預計時間: {questionCount * 2} 分鐘</span>
                      <span>{Math.max(1, maxQuestions)}</span>
                  </div>
              </div>
          </div>

          <button
            onClick={() => startQuiz(questionCount)}
            disabled={maxQuestions === 0}
            className={`w-full md:w-auto px-12 py-4 text-white text-xl font-bold rounded-full transition-all transform shadow-lg
                ${maxQuestions === 0 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-fudan-blue hover:bg-blue-800 hover:scale-105'
                }
            `}
          >
            開始測驗
          </button>
        </div>
      </div>
    </div>
  );
};

export const QuizPlayingView = ({ gameState, questions, formatTime, handleOptionSelect, prevQuestion, nextQuestion, finishQuiz, favoriteQuestionIds, toggleFavorite }: any) => {
    // --- Drawing State ---
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [isPaperOpen, setIsPaperOpen] = useState(false);
    const [currentTool, setCurrentTool] = useState<'pen' | 'eraser'>('pen');
    const [penColor, setPenColor] = useState('#EF4444'); // Red default
    const [brushSize, setBrushSize] = useState(3);
    const [eraserSizeIndex, setEraserSizeIndex] = useState(1);

    const mainCanvasRef = useRef<HTMLCanvasElement>(null);
    const mainContainerRef = useRef<HTMLDivElement>(null);
    const paperCanvasRef = useRef<HTMLCanvasElement>(null);
    const paperContainerRef = useRef<HTMLDivElement>(null);

    // In-memory cache for drawings (QuestionID -> ImageData)
    // Key format: `${questionId}_main` or `${questionId}_paper`
    const drawingCache = useRef<Record<string, ImageData>>({});

    const currentQ = questions[gameState.currentQuestionIndex];

    // -- Canvas Logic --
    const saveCanvasToCache = (layer: 'main' | 'paper', questionId: number) => {
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // Check if canvas is empty to avoid saving unnecessary data? 
        // For simplicity, just save.
        const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
        drawingCache.current[`${questionId}_${layer}`] = data;
    };

    const restoreCanvasFromCache = (layer: 'main' | 'paper', questionId: number) => {
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        const cachedData = drawingCache.current[`${questionId}_${layer}`];
        if (cachedData) {
            // Need to ensure canvas size matches cached data
            if (cachedData.width === canvas.width && cachedData.height === canvas.height) {
                ctx.putImageData(cachedData, 0, 0);
            } else {
                // If resized (responsive), scaling is complex for raw ImageData.
                // Simple workaround: Clear if size mismatch (rare in single session on mobile)
                // or implemented scaled drawing (advanced). 
                // For now, accepting clear on resize is better than distortion or error.
            }
        }
    };

    const initCanvasSize = (canvas: HTMLCanvasElement | null, container: HTMLDivElement | null) => {
        if (canvas && container) {
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
        }
    };

    // Initialize/Resize logic
    useEffect(() => {
        const handleResize = () => {
            initCanvasSize(mainCanvasRef.current, mainContainerRef.current);
            initCanvasSize(paperCanvasRef.current, paperContainerRef.current);
            if (currentQ) {
                restoreCanvasFromCache('main', currentQ.id);
                restoreCanvasFromCache('paper', currentQ.id);
            }
        };
        
        // Initial setup
        handleResize();
        
        window.addEventListener('resize', handleResize);
        // Also trigger on paper open/close transition end
        const timeout = setTimeout(handleResize, 350); 
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeout);
        };
    }, [isPaperOpen, currentQ]); // Re-run when paper toggles or question changes (size might change)

    // Save/Restore when switching questions
    // Using a ref to track previous question ID to save *before* switch
    const prevQuestionIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (prevQuestionIdRef.current !== null && prevQuestionIdRef.current !== currentQ.id) {
            // Save old
            // We can't access the canvas of the *previous* question easily if the DOM changed?
            // Actually, the DOM elements are reused. We just need to save BEFORE restoring new data.
            // But React useEffect runs AFTER render.
            // So we need to save in the cleanup function of the *previous* effect or logic?
            // BETTER: Save implicitly on every stroke? No, performance.
            // STRATEGY: We save explicitly before updating state in 'handleNavigation' (passed from parent).
            // But parent controls navigation.
            // LOCAL FIX: We use this effect to Restore. Saving is tricky if we don't control the trigger.
            // Alternative: Save current canvas content to cache whenever it changes? Or use layoutEffect?
            
            // Let's rely on the fact that `currentQ` changed.
            // The canvas is currently displaying the *previous* drawing (if React hasn't unmounted/remounted the DOM node completely).
            // If key prop is used on the container, DOM is reset.
            // If NO key prop, DOM is reused.
            // Assuming DOM reuse for the canvas.
        }
        
        // Restore new
        if (currentQ) {
            // We need to wait for DOM paint? No, ref should be stable.
            restoreCanvasFromCache('main', currentQ.id);
            restoreCanvasFromCache('paper', currentQ.id);
        }
        
        prevQuestionIdRef.current = currentQ ? currentQ.id : null;
    }, [currentQ]);

    // Hook into navigation buttons?
    // Since props `prevQuestion` and `nextQuestion` are passed in, we can wrap them.
    const handlePrev = () => {
        if (currentQ) {
            saveCanvasToCache('main', currentQ.id);
            saveCanvasToCache('paper', currentQ.id);
        }
        prevQuestion();
    };

    const handleNext = () => {
        if (currentQ) {
            saveCanvasToCache('main', currentQ.id);
            saveCanvasToCache('paper', currentQ.id);
        }
        nextQuestion();
    };

    // Drawing Event Handlers
    const eraseSpecificColor = (ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, targetRgb: {r: number, g: number, b: number}) => {
        const startX = Math.floor(Math.max(0, x - radius));
        const startY = Math.floor(Math.max(0, y - radius));
        const endX = Math.ceil(Math.min(ctx.canvas.width, x + radius));
        const endY = Math.ceil(Math.min(ctx.canvas.height, y + radius));
        const w = endX - startX;
        const h = endY - startY;
        if (w <= 0 || h <= 0) return;
        const imageData = ctx.getImageData(startX, startY, w, h);
        const data = imageData.data;
        const rSq = radius * radius;
        for (let row = 0; row < h; row++) {
            for (let col = 0; col < w; col++) {
                const dx = col + startX - x;
                const dy = row + startY - y;
                if (dx * dx + dy * dy <= rSq) {
                    const i = (row * w + col) * 4;
                    const r = data[i]; const g = data[i + 1]; const b = data[i + 2]; const a = data[i + 3];
                    if (a > 0) {
                        const dist = (r - targetRgb.r) ** 2 + (g - targetRgb.g) ** 2 + (b - targetRgb.b) ** 2;
                        if (dist < 3500) { data[i + 3] = 0; }
                    }
                }
            }
        }
        ctx.putImageData(imageData, startX, startY);
    };

    const handleStartDrawing = (e: React.MouseEvent | React.TouchEvent, layer: 'main' | 'paper') => {
        if (!isDrawingMode) return;
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        const ctx = canvas?.getContext('2d', { willReadFrequently: true });
        if (!canvas || !ctx) return;
        
        const getPos = (evt: React.MouseEvent | React.TouchEvent) => {
            const rect = canvas.getBoundingClientRect();
            if ('touches' in evt) { return { x: evt.touches[0].clientX - rect.left, y: evt.touches[0].clientY - rect.top }; }
            return { x: (evt as React.MouseEvent).clientX - rect.left, y: (evt as React.MouseEvent).clientY - rect.top };
        };
        const startPos = getPos(e);
        let lastX = startPos.x; let lastY = startPos.y;
        const targetRgb = currentTool === 'eraser' ? hexToRgb(penColor) : null;
        const eraserRadius = ERASER_SIZES[eraserSizeIndex];
        
        if (currentTool === 'pen') {
            ctx.beginPath(); ctx.moveTo(lastX, lastY); ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = brushSize; ctx.strokeStyle = penColor; ctx.globalCompositeOperation = 'source-over';
        } else { 
            if (targetRgb) eraseSpecificColor(ctx, lastX, lastY, eraserRadius, targetRgb); 
        }
        
        const draw = (moveEvent: MouseEvent | TouchEvent) => {
            moveEvent.preventDefault();
            const pos = getPos(moveEvent as any);
            if (currentTool === 'pen') { 
                ctx.lineTo(pos.x, pos.y); ctx.stroke(); 
            } else if (currentTool === 'eraser' && targetRgb) {
                const dist = Math.hypot(pos.x - lastX, pos.y - lastY);
                const steps = Math.ceil(dist / (eraserRadius / 2)); 
                for (let i = 1; i <= steps; i++) {
                    const ix = lastX + (pos.x - lastX) * (i / steps);
                    const iy = lastY + (pos.y - lastY) * (i / steps);
                    eraseSpecificColor(ctx, ix, iy, eraserRadius, targetRgb);
                }
            }
            lastX = pos.x; lastY = pos.y;
        };
        const stop = () => {
            if (currentTool === 'pen') ctx.closePath();
            // Optional: Auto-save on stop to ensure data isn't lost if they navigate immediately
            if (currentQ) saveCanvasToCache(layer, currentQ.id);
            
            canvas.removeEventListener('mousemove', draw); canvas.removeEventListener('mouseup', stop);
            canvas.removeEventListener('touchmove', draw); canvas.removeEventListener('touchend', stop);
        };
        canvas.addEventListener('mousemove', draw); canvas.addEventListener('mouseup', stop);
        canvas.addEventListener('touchmove', draw, { passive: false }); canvas.addEventListener('touchend', stop);
    };

    const clearActiveCanvas = () => {
        const layer = isPaperOpen ? 'paper' : 'main';
        const canvas = layer === 'main' ? mainCanvasRef.current : paperCanvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            if (currentQ) {
                delete drawingCache.current[`${currentQ.id}_${layer}`];
            }
        }
    };

    const handleEraserClick = () => { 
        if (currentTool === 'eraser') { setEraserSizeIndex(prev => (prev + 1) % ERASER_SIZES.length); } 
        else { setCurrentTool('eraser'); } 
    };
    
    const dynamicCursor = isDrawingMode ? (currentTool === 'eraser' ? getEraserCursor(ERASER_SIZES[eraserSizeIndex]) : 'crosshair') : 'auto';

    if (!currentQ) return <div>Error loading question</div>;

    const isLastQ = gameState.currentQuestionIndex === questions.length - 1;
    const progress = ((gameState.currentQuestionIndex + 1) / questions.length) * 100;
    const tags = getTags(currentQ.category);
    const isFavorite = favoriteQuestionIds ? favoriteQuestionIds.includes(currentQ.id) : false;

    return (
      <div className="flex-1 bg-gray-100 flex flex-col items-center p-4 overflow-hidden relative">
        <div className="w-full max-w-3xl bg-white p-4 rounded-xl shadow-md flex justify-between items-center mb-6 sticky top-0 z-10 border border-gray-200 shrink-0">
          <div className="flex items-center text-fudan-blue font-bold text-xl font-mono">
            <Clock className={`w-6 h-6 mr-2 ${gameState.timeLeft < 60 ? 'text-red-600 animate-pulse' : ''}`} />
            <span className={gameState.timeLeft < 60 ? 'text-red-600' : ''}>
                {formatTime(gameState.timeLeft)}
            </span>
          </div>
          <div className="text-gray-600 font-bold">
            第 <span className="text-fudan-blue text-xl">{gameState.currentQuestionIndex + 1}</span> / {questions.length} 題
          </div>
        </div>

        <div className="w-full max-w-3xl h-2 bg-gray-300 rounded-full mb-6 overflow-hidden shrink-0">
          <div 
            className="h-full bg-fudan-blue transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Scratchpad (Fixed Bottom) */}
        <div ref={paperContainerRef} className={`fixed inset-x-0 bottom-0 bg-white z-40 border-t-2 border-gray-300 shadow-[0_-5px_15px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-in-out ${isPaperOpen ? 'translate-y-0' : 'translate-y-full'}`} style={{ height: '50%', backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px' }}>
            <div className="absolute top-2 right-2 text-xs text-gray-400 font-mono select-none z-50">計算紙</div>
            <canvas ref={paperCanvasRef} onMouseDown={(e) => handleStartDrawing(e, 'paper')} onTouchStart={(e) => handleStartDrawing(e, 'paper')} className={`absolute inset-0 z-40 touch-none ${isDrawingMode ? 'pointer-events-auto' : 'pointer-events-none'}`} style={{ cursor: dynamicCursor }} />
        </div>

        {/* Drawing Toolbar */}
        {isDrawingMode && (
            <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-full shadow-xl border border-gray-200 px-4 py-2 flex items-center space-x-4 animate-fade-in-up">
                <div className="flex space-x-2 border-r border-gray-200 pr-4">{['#000000', '#EF4444', '#3B82F6', '#10B981'].map(color => (<button key={color} onClick={() => { setPenColor(color); setCurrentTool('pen'); }} className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${penColor === color && currentTool === 'pen' ? 'border-gray-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: color }} />))}</div>
                <div className="relative flex flex-col items-center group"><button onClick={handleEraserClick} className={`p-2 rounded-full hover:bg-gray-100 relative ${currentTool === 'eraser' ? 'bg-gray-200 text-gray-800' : 'text-gray-500'}`} title="橡皮擦 (點擊切換大小)"><Eraser className="w-5 h-5" />{currentTool === 'eraser' && (<div className="absolute -top-1 -right-1 w-3 h-3 bg-fudan-blue rounded-full border border-white flex items-center justify-center"><span className="text-[8px] text-white font-bold">{['S','M','L'][eraserSizeIndex]}</span></div>)}</button></div>
                <button onClick={clearActiveCanvas} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-red-500" title={isPaperOpen ? "清空計算紙" : "清空卡片塗鴉"}><Trash2 className="w-5 h-5" /></button>
                <div className="w-px h-6 bg-gray-200"></div>
                <button onClick={() => setIsPaperOpen(!isPaperOpen)} className={`flex items-center px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${isPaperOpen ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{isPaperOpen ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronUp className="w-4 h-4 mr-1" />}計算紙</button>
            </div>
        )}

        <div className="w-full max-w-3xl bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col shrink-0 relative flex-1 min-h-0">
            {/* Card Content & Canvas Container */}
            <div className="relative flex-1 flex flex-col overflow-y-auto" ref={mainContainerRef}>
                
                {/* Main Overlay Canvas */}
                <canvas 
                    ref={mainCanvasRef} 
                    onMouseDown={(e) => handleStartDrawing(e, 'main')} 
                    onTouchStart={(e) => handleStartDrawing(e, 'main')} 
                    className={`absolute inset-0 z-30 touch-none ${isDrawingMode ? 'pointer-events-auto' : 'pointer-events-none'}`} 
                    style={{ cursor: dynamicCursor }} 
                />

                <button 
                    onClick={() => toggleFavorite && toggleFavorite(currentQ.id)}
                    className="absolute top-4 right-4 z-20 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    title={isFavorite ? "取消收藏" : "加入最愛"}
                >
                    <Star className={`w-6 h-6 ${isFavorite ? 'text-orange-500 fill-current' : 'text-gray-300'}`} />
                </button>

                <div className="bg-blue-50 px-6 py-3 border-b border-blue-100 flex justify-between items-center pr-16 shrink-0">
                    <div className="flex flex-wrap gap-2">
                        {tags.map((tag, idx) => (
                             <span key={idx} className="text-xs font-bold px-2 py-1 bg-fudan-blue text-white rounded uppercase tracking-wider flex items-center">
                                <Tag className="w-3 h-3 mr-1" /> {tag}
                             </span>
                        ))}
                    </div>
                    <span className="text-xs text-gray-500 font-mono">ID: {currentQ.id}</span>
                </div>

                <div className="p-6 md:p-10 pb-24"> {/* Added pb-24 to prevent options being hidden by toolbar */}
                    <h2 className="text-xl md:text-2xl font-serif text-gray-900 leading-relaxed font-medium mb-6 pr-4 relative z-10">
                        {currentQ.text}
                    </h2>
                    {currentQ.svg && (
                        <div className="mb-8 p-4 bg-gray-50 rounded-xl border border-gray-100 flex justify-center relative z-10">
                            {currentQ.svg}
                        </div>
                    )}
                    <div className="space-y-3 relative z-10">
                        {currentQ.options.map((option: string, idx: number) => {
                            const isSelected = gameState.userAnswers[currentQ.id] === idx;
                            return (
                                <button
                                    key={idx}
                                    onClick={() => !isDrawingMode && handleOptionSelect(idx)}
                                    disabled={isDrawingMode}
                                    className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center group
                                        ${isSelected 
                                            ? 'border-fudan-blue bg-blue-50 text-fudan-blue shadow-inner ring-1 ring-fudan-blue' 
                                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                        }
                                        ${isDrawingMode ? 'cursor-crosshair' : 'cursor-pointer'}
                                    `}
                                >
                                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center mr-4 font-bold text-sm transition-colors
                                         ${isSelected ? 'bg-fudan-blue border-fudan-blue text-white' : 'border-gray-300 text-gray-400 group-hover:border-blue-300'}
                                    `}>
                                        {isSelected ? <CheckCircle className="w-5 h-5" /> : String.fromCharCode(65 + idx)}
                                    </div>
                                    <span className={`text-lg font-serif math-font ${isSelected ? 'font-bold' : 'text-gray-800'}`}>{option}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center shrink-0 z-20 relative">
                <button
                    onClick={handlePrev}
                    disabled={gameState.currentQuestionIndex === 0}
                    className={`px-6 py-2 rounded-lg font-bold ${
                        gameState.currentQuestionIndex === 0 
                        ? 'text-gray-300 cursor-not-allowed' 
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    上一題
                </button>

                {/* Drawing Toggle Button */}
                <button 
                    onClick={() => setIsDrawingMode(!isDrawingMode)} 
                    className={`p-3 rounded-full transition-colors flex items-center ${isDrawingMode ? 'bg-yellow-500 text-white shadow-inner' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-100'}`}
                    title="啟用畫筆"
                >
                    <PenTool className="w-5 h-5 mr-2" />
                    <span className="text-sm font-bold">{isDrawingMode ? '關閉畫筆' : '計算/塗鴉'}</span>
                </button>

                {isLastQ ? (
                    <button onClick={finishQuiz} className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold shadow-md">交卷</button>
                ) : (
                    <button onClick={handleNext} className="px-8 py-3 bg-fudan-blue hover:bg-blue-800 text-white rounded-lg font-bold shadow-md">下一題</button>
                )}
            </div>
        </div>
      </div>
    );
};

export const QuizResultView = ({ gameState, questions, resetQuiz, calculateScore }: any) => {
    // ... [Result View same as before] ...
    const score = calculateScore();
    let feedback = '';

    if (score >= 90) feedback = '太強了！錄取資優班絕對沒問題！';
    else if (score >= 80) feedback = '表現優異，錄取有望！';
    else if (score >= 60) feedback = '及格邊緣，需要再加強計算細心度。';
    else feedback = '請再加油！基礎觀念需要重新複習。';

    return (
      <div className="flex-1 bg-gray-100 py-8 px-4 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden text-center">
                <div className={`p-8 ${score >= 60 ? 'bg-fudan-blue' : 'bg-red-600'} text-white`}>
                    <p className="opacity-80 font-bold tracking-wider mb-2">最終成績</p>
                    <div className="text-6xl font-black mb-4 font-mono">{score} <span className="text-2xl font-normal">/ {questions.length * 10}</span></div>
                    <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                        <Trophy className="w-5 h-5 mr-2" />
                        <span className="font-bold">{feedback}</span>
                    </div>
                </div>
            </div>
            
            <div className="flex items-center mb-4">
                <BookOpen className="w-6 h-6 text-gray-700 mr-2" />
                <h2 className="text-2xl font-bold text-gray-800">試題詳解與分析</h2>
            </div>

            <div className="space-y-6">
                {questions.map((q: Question, index: number) => {
                    const userAnswerIdx = gameState.userAnswers[q.id];
                    const isCorrect = userAnswerIdx === q.correctAnswerIndex;
                    return (
                        <div key={q.id} className={`bg-white rounded-xl shadow border-l-8 ${isCorrect ? 'border-green-500' : 'border-red-500'} overflow-hidden`}>
                            <div className="bg-gray-50 px-6 py-3 border-b border-gray-100 flex justify-between items-center">
                                <span className="font-bold text-gray-700">第 {index + 1} 題</span>
                                {isCorrect ? <span className="text-green-600 font-bold text-sm">答對 (+10分)</span> : <span className="text-red-600 font-bold text-sm">答錯</span>}
                            </div>
                            <div className="p-6">
                                <h3 className="text-lg font-serif font-bold text-gray-900 mb-4">{q.text}</h3>
                                {q.svg && <div className="mb-4 flex justify-start opacity-80 scale-75 origin-top-left -mb-6">{q.svg}</div>}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-sm">
                                    <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                        <span className="block text-gray-500 mb-1">你的答案</span>
                                        <span className={`font-bold text-lg ${isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                            {userAnswerIdx !== undefined ? `(${String.fromCharCode(65 + userAnswerIdx)}) ${q.options[userAnswerIdx]}` : '未作答'}
                                        </span>
                                    </div>
                                    <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
                                        <span className="block text-gray-500 mb-1">正確答案</span>
                                        <span className="font-bold text-lg text-blue-700">({String.fromCharCode(65 + q.correctAnswerIndex)}) {q.options[q.correctAnswerIndex]}</span>
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                                    <div className="flex items-center text-gray-800 font-bold mb-3">
                                        <AlertCircle className="w-5 h-5 mr-2 text-fudan-blue" /> 名師解析：
                                    </div>
                                    <div className="text-gray-700 leading-relaxed font-serif whitespace-pre-wrap pl-7">{q.explanation}</div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="text-center pt-8 pb-12">
                 <button onClick={resetQuiz} className="px-8 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-full font-bold shadow-lg">重新測驗</button>
            </div>
        </div>
      </div>
    );
};
